import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpCircle, Plus, Minus } from 'lucide-react';

const FAQ = () => {
  const navigate = useNavigate();

  const [openQuestions, setOpenQuestions] = React.useState<number[]>([]);

  const toggleQuestion = (index: number) => {
    if (openQuestions.includes(index)) {
      setOpenQuestions(openQuestions.filter(i => i !== index));
    } else {
      setOpenQuestions([...openQuestions, index]);
    }
  };

  const faqCategories = [
    {
      title: "Immigration & Legal",
      questions: [
        {
          q: "What are the main types of residence permits in Poland?",
          a: "The main types are temporary residence permits (valid for up to 3 years), permanent residence permits (indefinite), and EU long-term residence permits. The type you need depends on your purpose of stay (work, study, family, etc.)."
        },
        {
          q: "How long does it take to process a residence permit application?",
          a: "Processing times vary by voivodeship office and type of permit, but typically range from 2-6 months. Work-based permits often take 3-4 months. Complex cases may take longer."
        },
        {
          q: "Can I travel while my residence permit is being processed?",
          a: "Yes, if you have a valid visa or previous residence card. However, you should obtain a temporary residence certificate (zaświadczenie) before traveling, which confirms your application is being processed."
        }
      ]
    },
    {
      title: "Work & Employment",
      questions: [
        {
          q: "Do I need a work permit if I have a residence permit?",
          a: "It depends on your type of residence permit. Some permits (like EU Blue Card) include work authorization, while others require a separate work permit. EU citizens don't need work permits."
        },
        {
          q: "How can I validate my foreign qualifications in Poland?",
          a: "Professional qualifications can be recognized through ENIC-NARIC Poland. Academic degrees need to be validated through the Ministry of Education or specific universities."
        },
        {
          q: "What are the main types of employment contracts in Poland?",
          a: "The main types are umowa o pracę (employment contract), umowa zlecenie (contract of mandate), and umowa o dzieło (contract for specific work). Each has different tax and social security implications."
        }
      ]
    },
    {
      title: "Daily Life & Integration",
      questions: [
        {
          q: "How can I find accommodation in Poland?",
          a: "Popular options include real estate websites (otodom.pl, olx.pl), real estate agencies, and expat Facebook groups. Most rentals require a deposit equal to 1-2 months' rent."
        },
        {
          q: "How does the healthcare system work for foreigners?",
          a: "If you're employed, you're usually covered by the NFZ (National Health Fund). Private healthcare is also widely available. EU citizens can use their EHIC card for emergency care."
        },
        {
          q: "Do I need to learn Polish?",
          a: "While English is widely spoken in business and larger cities, knowing basic Polish will greatly help with daily life and integration. It's especially useful for dealing with government offices."
        }
      ]
    },
    {
      title: "Family & Education",
      questions: [
        {
          q: "Can my family join me in Poland?",
          a: "Yes, through family reunification. You need to prove stable income and accommodation. The process is easier for EU citizens and permanent residents."
        },
        {
          q: "What are the options for international education?",
          a: "Major cities have international schools offering IB and various national curricula. Public schools are free but teach in Polish. Some universities offer English-language programs."
        },
        {
          q: "How can I register a newborn child in Poland?",
          a: "Births must be registered within 21 days at the local Civil Registry Office (Urząd Stanu Cywilnego). You'll need parents' documents and the hospital birth certificate."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <HelpCircle className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Common questions about immigration and life in Poland
          </p>
        </div>

        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <Card key={categoryIndex}>
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">{category.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {category.questions.map((qa, questionIndex) => {
                  const index = categoryIndex * 10 + questionIndex;
                  const isOpen = openQuestions.includes(index);
                  return (
                    <div key={questionIndex} className="border-b border-border last:border-0 pb-4 last:pb-0">
                      <button
                        onClick={() => toggleQuestion(index)}
                        className="w-full text-left flex items-center justify-between py-2 text-foreground hover:text-primary transition-colors"
                      >
                        <span className="font-medium">{qa.q}</span>
                        {isOpen ? (
                          <Minus className="h-5 w-5 flex-shrink-0" />
                        ) : (
                          <Plus className="h-5 w-5 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <p className="mt-2 text-muted-foreground">
                          {qa.a}
                        </p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/knowledge-base/support-resources')}
            className="text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Support Resources
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 